import { Dayjs } from 'dayjs';
import * as Yup from 'yup';
import { apiTimeStringFormat, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { City, Country, Location, LocationRaw, LocationType } from '@api/Trinium/Endpoints/Location/types';
import { Contact } from '@api/Trinium/Endpoints/Contact/types';

export interface ConsingneeModalProps {
  /** Id of the Location to edit, or undefined to add new */
  id?: string;
  title?: string;
  enableSelection: boolean;
  /** When provided, will plug into the mutation hook so the calling component can watch for a response */
  apiFixedCacheKey?: string;
  /** Default values to pre-populate the form, useful when the form is used for something other than editing a Location */
  defaultValues?: Partial<ConsigneeFormik>;
  /** When editing from a leg, the job whose legs should be updated with the saved name and address. Leave off everywhere else. */
  updateLegsForJobId?: number;
}

export type ConsigneeFormik = Record<string, string> & {
  id?: string;
  isTrmUnique?: boolean;
  name?: string;
  type?: LocationType;
  address1?: string;
  address2?: string;
  city?: City | null;
  zip?: string;
  country?: Country | null;
  area?: string;
  phone?: string;
  fax?: string;
  email?: string;
  notes?: string;

  shippingHours: OperatingHoursFormik;
  deliveryHours: OperatingHoursFormik;

  puNotes?: string;
  usePuNotes?: boolean;
  delvNotes?: string;
  useDelvNotes?: boolean;
  contact?: Contact[];
};

export const ConsigneeFormikValidationSchema = Yup.object<ConsigneeFormik>().shape({
  // Phone/Fax/Email are validated when they are editable: add flow or terminal-unique edit flow.
  phone: Yup.string().when(['id', 'isTrmUnique'], {
    is: (id?: string, isTrmUnique?: boolean) => !id || Boolean(isTrmUnique),
    then: (schema) => schema.min(10, 'Please enter valid Phone Number')
  }),
  fax: Yup.string().when(['id', 'isTrmUnique'], {
    is: (id?: string, isTrmUnique?: boolean) => !id || Boolean(isTrmUnique),
    then: (schema) => schema.min(10, 'Please enter valid Fax Number')
  }),
  email: Yup.string().when(['id', 'isTrmUnique'], {
    is: (id?: string, isTrmUnique?: boolean) => !id || Boolean(isTrmUnique),
    then: (schema) =>
      schema.test('emailValidation', 'Please enter one or more valid Email Addresses', (value) => {
        if (!value) return true;
        const emails = value.split(',');
        return emails.every((email) =>
          Yup.string()
            .email()
            .matches(/^[^@]+@[^@]+\.[^@]+$/)
            .isValidSync(email)
        );
      })
  })
});

export type OperatingHoursFormik = Record<string, string> & {
  weekdayFrom: Dayjs | null;
  weekdayTo: Dayjs | null;
  saturdayFrom: Dayjs | null;
  saturdayTo: Dayjs | null;
  sundayFrom: Dayjs | null;
  sundayTo: Dayjs | null;
};

export const operatingHoursFormValidation = Yup.object<OperatingHoursFormik>().shape({
  weekdayFrom: Yup.date().nullable().typeError('Invalid Time'),
  weekdayTo: Yup.date().nullable().typeError('Invalid Time').min(Yup.ref('weekdayFrom'), 'To Time must be after From Time'),
  saturdayFrom: Yup.date().nullable().typeError('Invalid Time'),
  saturdayTo: Yup.date().nullable().typeError('Invalid Time').min(Yup.ref('saturdayFrom'), 'To Time must be after From Time'),
  sundayFrom: Yup.date().nullable().typeError('Invalid Time'),
  sundayTo: Yup.date().nullable().typeError('Invalid Time').min(Yup.ref('sundayFrom'), 'To Time must be after From Time')
});

const shippingHoursApiFieldMappings: Record<keyof OperatingHoursFormik, string> = {
  // Direct properties of the Consingment Formik and there API field name counter-part
  weekdayFrom: 'puWkFrom',
  weekdayTo: 'puWkTo',
  saturdayFrom: 'puSatFrom',
  saturdayTo: 'puSatFrom',
  sundayFrom: 'puSunFrom',
  sundayTo: 'puSunFrom'
};

const deliveryHoursApiFieldMappings: Record<keyof OperatingHoursFormik, string> = {
  // Direct properties of the Consingment Formik and there API field name counter-part
  weekdayFrom: 'delvWkFrom',
  weekdayTo: 'delvWkTo',
  saturdayFrom: 'delvSatFrom',
  saturdayTo: 'delvSatFrom',
  sundayFrom: 'delvSunFrom',
  sundayTo: 'delvSunFrom'
};

export const consigneeApiFieldMappings: Record<keyof ConsigneeFormik, string | Record<string, string>> = {
  // Direct properties of the Consingment Formik and there API field name counter-part
  name: 'name',
  type: 'type',
  address1: 'addr1',
  address2: 'addr2',
  zip: 'zip',
  phone: 'phone',
  fax: 'fax',
  email: 'emailAddr',
  notes: 'notes',

  // Nested object...
  city: 'cityId',
  // country?: Country

  // Sub-forms that use their own mappings
  shippingHours: shippingHoursApiFieldMappings,
  deliveryHours: deliveryHoursApiFieldMappings
};

export function mapLocationToConsigneeForm(location: Location) {
  return {
    id: location.id,
    isTrmUnique: location.isTrmUnique,
    name: location.name,
    type: location.type,
    address1: location.addr1,
    address2: location.addr2,
    city: location.locCity,
    zip: location.zip,
    country: location.country,
    area: location.areaName,
    phone: location.phone ?? '',
    fax: location.fax ?? '',
    email: location.emailAddr ?? '',
    notes: location.notes ?? '',
    shippingHours: {
      weekdayFrom: parseDayJs(location.puWkFrom),
      weekdayTo: parseDayJs(location.puWkTo),
      saturdayFrom: parseDayJs(location.puSatFrom),
      saturdayTo: parseDayJs(location.puSatTo),
      sundayFrom: parseDayJs(location.puSunFrom),
      sundayTo: parseDayJs(location.puSunTo)
    },
    deliveryHours: {
      weekdayFrom: parseDayJs(location.delvWkFrom),
      weekdayTo: parseDayJs(location.delvWkTo),
      saturdayFrom: parseDayJs(location.delvSatFrom),
      saturdayTo: parseDayJs(location.delvSatTo),
      sundayFrom: parseDayJs(location.delvSunFrom),
      sundayTo: parseDayJs(location.delvSunTo)
    },
    usePuNotes: location.usePuNotes ?? false,
    puNotes: location.puNotes ?? '',
    useDelvNotes: location.useDelvNotes ?? false,
    delvNotes: location.delvNotes ?? '',
    contact: location.contact ?? []
  } as ConsigneeFormik;
}

/**
 * Function to convert the dirty time input into the API value that should be passed in
 * @param value The Current Value that is already determined dirty
 * @returns
 */
function convertFormTimeToApiRequest(value: Dayjs | null | undefined): string | null | undefined {
  /** If the value has a time, format it */
  if (value?.isValid) {
    return value.format(apiTimeStringFormat);
  }
  /** If value is undefined, don't include. Otherwise, set null to intend user blanked out the existing value */
  return value === null ? null : undefined;
}

export function mapConsigneeFormToLocation(form: Partial<ConsigneeFormik>) {
  const updates: Partial<LocationRaw> & Record<string, string | boolean | LocationType | undefined | null> = {
    id: form.id,
    name: form.name,
    type: form.type,
    addr1: form.address1,
    addr2: form.address2,
    cityId: form.city?.id,
    zip: form.zip,
    cntryId: form.country?.id,
    emailAddr: form.email,
    phone: form.phone,
    fax: form.fax,
    notes: form.notes,
    puWkFrom: convertFormTimeToApiRequest(form.shippingHours?.weekdayFrom),
    puWkTo: convertFormTimeToApiRequest(form.shippingHours?.weekdayTo),
    puSatFrom: convertFormTimeToApiRequest(form.shippingHours?.saturdayFrom),
    puSatTo: convertFormTimeToApiRequest(form.shippingHours?.saturdayTo),
    puSunFrom: convertFormTimeToApiRequest(form.shippingHours?.sundayFrom),
    puSunTo: convertFormTimeToApiRequest(form.shippingHours?.sundayTo),
    delvWkFrom: convertFormTimeToApiRequest(form.deliveryHours?.weekdayFrom),
    delvWkTo: convertFormTimeToApiRequest(form.deliveryHours?.weekdayTo),
    delvSatFrom: convertFormTimeToApiRequest(form.deliveryHours?.saturdayFrom),
    delvSatTo: convertFormTimeToApiRequest(form.deliveryHours?.saturdayTo),
    delvSunFrom: convertFormTimeToApiRequest(form.deliveryHours?.sundayFrom),
    delvSunTo: convertFormTimeToApiRequest(form.deliveryHours?.sundayTo),
    puNotes: form.puNotes,
    usePuNotes: form.usePuNotes,
    delvNotes: form.delvNotes,
    useDelvNotes: form.useDelvNotes
  };

  Object.keys(updates).forEach((key) => {
    // If the value is undefined than remove it from the request
    if (updates[key] === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete updates[key];
    }
  });

  return updates as Partial<LocationRaw>;
}
